'use client'

import { graphql } from '@/generated/gql'
import { ServiceType, type VendorHighlightCategoriesFilterInput } from '@/generated/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { useLazyQuery } from '@apollo/client'

import { useCallback, useState } from 'react'

import type { VendorSearchState } from './useVendorSearchState'

const vendorHighlightsQuery = graphql(`
  query VendorHighlights_Query(
    $location: Coordinates!
    $filters: VendorHighlightCategoriesFilterInput!
    $limit: Int
    $offset: Int
    $lat: Float!
    $lng: Float!
    $serviceType: ServiceType!
    $dateTime: VendorListDateSearchInput
  ) {
    vendorHighlightsForLocation(
      location: $location
      filters: $filters
      limit: $limit
      offset: $offset
    ) {
      pageInfo {
        hasMore
        count
      }
      highlightCategories {
        ...HighlightCategoryWithVendors_Fragment
      }
    }
  }
`)

const sanitizeTags = (tags?: string | (string | null | undefined)[] | null) => {
  if (!tags) return undefined
  if (Array.isArray(tags)) {
    const cleaned = tags.filter((tag): tag is string => Boolean(tag))
    return cleaned.length ? cleaned : undefined
  }
  return tags ? [tags] : undefined
}

const sanitizeString = (value?: string | null) => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length ? trimmed : undefined
}

export const buildHighlightFilters = (
  tenantId: string,
  searchFilters: VendorSearchState['filters'],
): VendorHighlightCategoriesFilterInput => {
  return {
    tenantId,
    serviceType: searchFilters.serviceType ?? ServiceType.Gm,
    search: sanitizeString(searchFilters.search ?? undefined),
    headCount: searchFilters.headCount ?? undefined,
    location: searchFilters.location ?? undefined,
    minDateTime: searchFilters.minDateTime ?? undefined,
    dateTime: searchFilters.dateTime ?? undefined,
    supplierType: searchFilters.supplierType ?? undefined,
    cuisineTags: sanitizeTags(searchFilters.cuisineTags ?? null),
    dietaryTags: sanitizeTags(searchFilters.dietaryTags ?? null),
    highlightsTags: sanitizeTags(searchFilters.highlightsTags ?? null),
    corporateTags: sanitizeTags(searchFilters.corporateTags ?? null),
    keyDatesTags: sanitizeTags(searchFilters.keyDatesTags ?? null),
    occasionTags: sanitizeTags(searchFilters.occasionTags ?? null),
    diversityOfOwnershipTags: sanitizeTags(searchFilters.diversityOfOwnershipTags ?? null),
    bestForTags: sanitizeTags(searchFilters.bestForTags ?? null),
    itemSearch: sanitizeString(searchFilters.itemSearch ?? undefined),
    getStats: true,
    isActive: true,
    isDraft: false,
    isGMActive: true,
  }
}

export const useVendorHighlightCategories = () => {
  const showHighlights = useFeatureFlag(SPLITS.SHOW_HIGHLIGHTS_GM)
  const tenant = useTenant()

  const [getHighlightData, highlightsData] = useLazyQuery(vendorHighlightsQuery, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })
  const [highlightFilters, setHighlightFilters] =
    useState<VendorHighlightCategoriesFilterInput | null>(null)

  const fetchHighlights = useCallback(
    (filters: VendorSearchState['filters']) => {
      if (!showHighlights) return
      const lat = filters.location?.latitude
      const lng = filters.location?.longitude
      if (typeof lat !== 'number' || typeof lng !== 'number') return

      const nextFilters = buildHighlightFilters(tenant.id, filters)
      setHighlightFilters(nextFilters)
      const serviceType = nextFilters.serviceType ?? ServiceType.Gm
      const dateTime = filters.dateTime ?? null

      getHighlightData({
        variables: {
          location: { latitude: lat, longitude: lng },
          filters: nextFilters,
          limit: 10,
          offset: 0,
          lat,
          lng,
          serviceType,
          dateTime,
        },
      })
    },
    [getHighlightData, showHighlights, tenant.id],
  )

  return { ...highlightsData, highlightFilters, fetchHighlights }
}
